import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SideNavLayout from "../customsidenav/sidenavlayout";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import calendericon from "../assets/auth/calendericon.svg";
import React, { useEffect, useState, useMemo, useRef } from "react";
import TabComponent from "./tabs/tab";
import FaxTable from "./faxtable";
import EmailTable from "./emailtable";
import VoiceMailTable from "./voicemailtable";
import { styled } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DateCalendar } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { reseticongray } from "../utils/svgs/svgcode";
import { externalapi } from "../api/externalapi";
import { leftarrow, rightarrow } from "../colorcoding/svg/iconsvg";
import api from "../api/api";
import { actionDropDownCalenderIcon } from "../landingscreen/modals/svgs";
import DateRangeSelector from "../daterangepicker/DateRangeSelector";
import Messaging from "./messaging";

const CustomDateCalendar = styled(DateCalendar)(({ theme }) => ({
  "& .MuiPickersCalendarHeader-label": {
    fontSize: "15px",
  },
  // Change selected date color
  "& .MuiPickersDay-root.Mui-selected": {
    backgroundColor: "#1D2951",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#1D2951", // Keep the same color on hover
    },
  },
}));

const Fax = () => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: dayjs(),
    endDate: dayjs(),
  });
  const [voicemails, setVoicemails] = useState([]);
  const [fax, setFax] = useState([]);
  const [emails, setEmails] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const practiseid = window.getPracticeId();

  const calendarRef = useRef(null);
  const calendarButtonRef = useRef(null);

  const tabs = [
    { label: "Fax", width: 64 },
    { label: "Email", width: 44 },
    { label: "Voice mail", width: 64 },
    { label: "Messaging", width: 74 },
  ];

  // Fetch Emails
  const fetchemails = async (startDate = null, endDate = null, search = "") => {
    try {
      setLoading(true);
      const payload = {
        startDate: startDate || dayjs().format("YYYY-MM-DD"),
        endDate: endDate || dayjs().format("YYYY-MM-DD"),
        recordSearch: search.trim(),
      };
      const response = await api.post(
        `/v1/${practiseid}/email/get-list`,
        payload,
      );
      if (response.status === 200) {
        setEmails(response.data || []);
      } else setEmails([]);
    } catch {
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Voicemails
  const fetchVoicemails = async (isblock) => {
    try {
      if (!isblock) {
        setLoading(true);
      }
      const extensionIds = [
        "62906620031",
        "62906621031",
        "348669030",
        "62906617031",
      ];
      const results = await Promise.allSettled(
        extensionIds.map((id) =>
          externalapi.post("/ringcentral/voice/getlist/", { extensionid: id }),
        ),
      );
      const allRecords = results.flatMap((res) =>
        res.status === "fulfilled" && res.value.status === 200
          ? res.value.data.data.records || []
          : [],
      );
      allRecords.sort(
        (a, b) => new Date(b.creationTime) - new Date(a.creationTime),
      );
      setVoicemails(allRecords);
    } catch {
      setVoicemails([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Fax
  const fetchFax = async (isblock) => {
    try {
      if (!isblock) {
        setLoading(true);
      }
      const response = await api.get(`v1/${practiseid}/fax/get-list`);
      // if(response.status){

      //       setFax(response?.data||[]);
      // }else{
      //   setFax([])
      // }
      if (response.status === 200) {
        const formatted = (response?.data || []).map((f) => ({
          ...f, // keep id, status etc
          ...f.records, // flatten records into root
          extensionId: f.records?.extensionId || f.extentionid,
        }));

        setFax(formatted);
      } else {
        setFax([]);
      }
    } catch {
      setFax([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on tab change
  useEffect(() => {
    document.title = "CHIM - Communication";
    setSearchTerm("");
    setSelectedDate(null);
    if (selectedTab === 3) {
      // Messaging tab - no fetch needed
    } else if (selectedTab === 1) fetchemails();
    else if (selectedTab === 2) fetchVoicemails();
    else if (selectedTab === 0) fetchFax();
  }, [selectedTab]);

  // Refetch Emails when date or searchTerm changes
  useEffect(() => {
    if (selectedTab === 1) {
      fetchemails(
        dateRange.startDate.format("YYYY-MM-DD"),
        dateRange.endDate.format("YYYY-MM-DD"),
        searchTerm,
      );
    }
  }, [selectedTab, dateRange, searchTerm]);

  // Filtered Voicemails
  const filteredVoicemails = useMemo(() => {
    let data = [...voicemails];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      data = data.filter(
        (vm) =>
          vm.from?.phoneNumber?.toLowerCase().includes(term) ||
          vm.from?.name?.toLowerCase().includes(term),
      );
    }
    if (selectedDate) {
      data = data.filter((vm) =>
        dayjs(vm.creationTime).isSame(selectedDate, "day"),
      );
    }
    return data;
  }, [voicemails, searchTerm, selectedDate]);

  // Filtered Fax
  const filteredFax = useMemo(() => {
    let data = [...fax];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      data = data.filter(
        (fx) =>
          fx.from?.phoneNumber?.toLowerCase().includes(term) ||
          fx.from?.name?.toLowerCase().includes(term) ||
          (fx.subject && fx.subject.toLowerCase().includes(term)),
      );
    }
    if (selectedDate) {
      data = data.filter((fx) =>
        dayjs(fx.creationTime).isSame(selectedDate, "day"),
      );
    }
    return data;
  }, [fax, searchTerm, selectedDate]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setIsCalendarOpen(false);
  };
  const clearDateFilter = () => setSelectedDate(null);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        calendarButtonRef.current &&
        !calendarButtonRef.current.contains(event.target)
      ) {
        setIsCalendarOpen(false);
      }
    };
    if (isCalendarOpen)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCalendarOpen]);
  const handlePrevDate = (e) => {
    e.stopPropagation(); // don’t open calendar
    setSelectedDate((prev) =>
      prev ? prev.subtract(1, "day") : dayjs().subtract(1, "day"),
    );
  };

  const handleNextDate = (e) => {
    e.stopPropagation(); // don’t open calendar
    setSelectedDate((prev) =>
      prev ? prev.add(1, "day") : dayjs().add(1, "day"),
    );
  };

  return (
    <div className="h-full">
      {selectedTab === 3 && (
        <style>{`
          body { overflow: hidden !important; }
          [style*="min-height: 100vh"] { height: 100vh !important; overflow: hidden !important; }
        `}</style>
      )}
      <SideNavLayout>
        {/* Search - hidden for Messaging tab */}
        {selectedTab !== 3 && (
          <div className="w-full flex justify-end gap-1 mb-3 relative">
            <div className="relative flex justify-end items-center w-full max-w-md">
              <div className="flex items-center w-[80%] bg-[#F5F6FA] border border-[#D5D5D5] rounded-[15px]">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="text-gray-400 ml-3"
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-[#F5F6FA] flex-grow py-1 pl-2 pr-3 text-base outline-none rounded-[15px]"
                  placeholder={
                    selectedTab === 1
                      ? "Search by sender or subject"
                      : "Search by phone, name, or subject"
                  }
                />
              </div>
            </div>
            <div
              className="flex gap-2 text-md cursor-pointer items-center"
              onClick={() => {
                setSearchTerm("");
                setSelectedDate(null);
              }}
            >
              {reseticongray}
              <div className="text-[#7B7575]">Reset</div>
            </div>
          </div>
        )}
        {/* Date Picker & Chip */}
        {selectedTab !== 3 && (
          <div className="flex flex-col gap-4 items-start mb-3 relative">
            {/* BUTTON */}
            <button
              ref={calendarButtonRef}
              onClick={() => setIsCalendarOpen(!isCalendarOpen)}
              className="flex items-center justify-between gap-2 px-3 py-2 bg-white rounded-[16px] shadow-md"
            >
              {selectedTab === 1 ? (
                <div className="flex flex-col items-end">
                  {/* <span className="text-[11px] text-[#6B7280]">Date Range</span> */}
                  <span className="text-[14px] font-bold">
                    {dateRange.startDate.format("MM-DD-YYYY")} -{" "}
                    {dateRange.endDate.format("MM-DD-YYYY")}
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-end">
                  <span className="text-[11px] text-[#6B7280]">
                    {(selectedDate ? selectedDate.isSame(dayjs(), "day") : true)
                      ? "Today"
                      : selectedDate.format("dddd")}
                  </span>
                  <span className="text-[14px] font-bold">
                    {(selectedDate || dayjs()).format("MM-DD-YYYY")}
                  </span>
                </div>
              )}

              <div className="text-[#9CA3AF] text-xl">
                {actionDropDownCalenderIcon}
              </div>
            </button>

            {/* CALENDAR POPUP */}
            {isCalendarOpen && (
              <div
                ref={calendarRef}
                className="absolute top-[60px] left-0 z-50 bg-white shadow-lg rounded-xl"
              >
                {selectedTab === 1 ? (
                  // 🟢 EMAIL → DATE RANGE
                  <DateRangeSelector
                    selectedDateRange={{
                      startDate: dateRange.startDate.toDate(),
                      endDate: dateRange.endDate.toDate(),
                      key: "selection",
                    }}
                    setSelectedDateRange={(range) =>
                      setDateRange({
                        startDate: dayjs(range.startDate),
                        endDate: dayjs(range.endDate),
                      })
                    }
                    onSubmit={() => setIsCalendarOpen(false)}
                    handlereset={() =>
                      setDateRange({
                        startDate: dayjs(),
                        endDate: dayjs(),
                      })
                    }
                  />
                ) : (
                  // 🟢 FAX & VOICEMAIL → SINGLE DATE
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="w-[300px]">
                      <CustomDateCalendar
                        value={selectedDate}
                        onChange={(date) => {
                          setSelectedDate(date);
                          setIsCalendarOpen(false);
                        }}
                      />
                    </div>
                  </LocalizationProvider>
                )}
              </div>
            )}
          </div>
        )}
        {/* Tabs + Reset */}
        <div className="flex justify-between">
          <TabComponent
            tabs={tabs}
            selectedTab={selectedTab}
            onSelectTab={setSelectedTab}
          />
        </div>

        {/* Tables */}
        <div className="w-full flex mt-4 justify-center">
          {selectedTab === 1 && (
            <EmailTable
              loading={loading}
              emails={emails}
              refresh={fetchemails}
            />
          )}
          {selectedTab === 2 && (
            <VoiceMailTable
              loading={loading}
              voicemails={filteredVoicemails}
              fetchVoicemail={fetchVoicemails}
            />
          )}
          {selectedTab === 0 && (
            <FaxTable fetchFax={fetchFax} loading={loading} fax={filteredFax} />
          )}
          {selectedTab === 3 && <Messaging onClose={() => setSelectedTab(0)} />}
        </div>
      </SideNavLayout>
    </div>
  );
};

export default Fax;
